import BlockIcon from "@mui/icons-material/Block";
import Check from "@mui/icons-material/Check";
import Error from "@mui/icons-material/Error";
import FlagIcon from "@mui/icons-material/Flag";
import HourglassTop from "@mui/icons-material/HourglassTop";
import Lock from "@mui/icons-material/Lock";
import OutlinedFlagIcon from "@mui/icons-material/OutlinedFlag";
import QuestionMark from "@mui/icons-material/QuestionMark";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import { Box, Link, Stack, Tooltip, Typography } from "@mui/material";
import { GRID_CHECKBOX_SELECTION_COL_DEF } from "@mui/x-data-grid-premium";
import { ControlledBigTooltip } from "components/ControlledBigTooltip";
import { FacilitiesExportButton } from "components/FacilitiesExportButton";
import { Importer } from "components/Importer";
import { IssueCount } from "components/IssueCount";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { StatusChip, type StatusChipColor } from "components/StatusChip";
import { TagsDisplay } from "components/TagsDisplay";
import {
  FacilityAlternateIdKnownKind,
  hasStateId,
  stateRequiresStateId,
} from "encamp-shared/src/facility/alternateId";
import {
  EPA_ID_REQUIREMENT_REASON_LABELS,
  epaIdRequirementStatusLabel,
} from "encamp-shared/src/facility/epaIdRequirementStatus";
import {
  findAlternateId,
  findAlternateIds,
  isAlternateIdExpired,
} from "encamp-shared/src/facilityAlternateId/findAlternateId";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { OneSchemaTemplateType } from "encamp-shared/src/oneSchema/types";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import {
  CollectionMode,
  CredentialKind,
  CredentialVerificationStatus,
  EpaIdRequirementStatus,
  FacilityStatus,
  FeatureFlagOverride,
  ImplementationFacilitiesQuery,
} from "generated-graphql/graphql";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { compact } from "lodash-es";
import { type MouseEvent, type ReactNode, useMemo, useState } from "react";
import { credentialKindToLabel } from "util/constants";
import { prettyPrintDateTime } from "util/dates";

import { FacilitiesBulkActionsMenu } from "./FacilitiesBulkActionMenu";

const FACILITIES_WITH_JOBS_QUERY = gql(`
  query ImplementationFacilities($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    facilities(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        customerFacilityId
        name
        status
        state
        collectionMode
        streetAddress1
        streetAddress2
        city
        zip
        state
        country
        wasteFeatureFlagOverride
        chemicalFeatureFlagOverride
        epaIdRequirementStatus
        epaIdRequirementReasons
        hasTierIICredential
        tenant {
          id
          name
        }
        jobs(action: extractData, sortDirection: DESC) {
          id
          action
          status
          reportingYear
          requestId
          facilityId
          reportId
          createdAt
          updatedAt
        }
        facilityAlternateIds {
          id
          type
          value
          hasNoId
          expiresAt
        }
        facilityCredentials {
          id
          credential {
            id
            verificationStatus
            lastVerificationCheckAt
            kind
          }
        }
        tags {
          id
          name
        }
        issueCount
        updatedAt
        createdAt
      }
      count
    }
  }
`);

export type Row = ImplementationFacilitiesQuery["facilities"]["items"][number];

function featureFlagOverrideLabel(value: string): string {
  if (value === FeatureFlagOverride.Inherit) {
    return "Inherit from Organization setting";
  }
  return prettyPrintEnumValue(value);
}

const EPA_ID_REQUIREMENT_STATUS_CHIP_COLORS: Record<EpaIdRequirementStatus, StatusChipColor> = {
  [EpaIdRequirementStatus.HasEpaId]: "success",
  [EpaIdRequirementStatus.NeedsEpaId]: "warning",
  [EpaIdRequirementStatus.EpaIdExpired]: "error",
  [EpaIdRequirementStatus.NotRequired]: "default",
  [EpaIdRequirementStatus.NotRequiredHasEpaId]: "info",
};

function EpaIdRequirementStatusCell({ row }: { row: Row }) {
  const copyToClipboard = useCopyToClipboard();
  const [copied, setCopied] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const status = row.epaIdRequirementStatus;
  if (!status) {
    return null;
  }

  const closeTooltip = async () => {
    setTooltipOpen(false);
    // Let the tooltip fade before the title flips back (as CopyableTypography does).
    await new Promise((resolve) => setTimeout(resolve, 200));
    setCopied(false);
  };
  const tooltip = epaIdRequirementStatusTooltip(row, copied);
  const handleCopy = async (event: MouseEvent<HTMLDivElement>) => {
    // Don't let the click also toggle the grid row selection.
    event.stopPropagation();
    event.preventDefault();
    await copyToClipboard(tooltip?.copyText ?? "");
    setCopied(true);
  };
  const chip = (
    <StatusChip
      color={EPA_ID_REQUIREMENT_STATUS_CHIP_COLORS[status]}
      label={epaIdRequirementStatusLabel(status)}
      onMouseEnter={tooltip ? () => setTooltipOpen(true) : undefined}
      onMouseLeave={tooltip ? closeTooltip : undefined}
      onClick={tooltip?.copyText ? handleCopy : undefined}
    />
  );
  if (!tooltip) {
    return chip;
  }

  return (
    <ControlledBigTooltip
      open={tooltipOpen}
      onClose={closeTooltip}
      placement="top-end"
      title={tooltip.title}
    >
      {chip}
    </ControlledBigTooltip>
  );
}

/** Tooltip content per status; null when there is nothing to show. */
function epaIdRequirementStatusTooltip(
  row: Row,
  copied: boolean
): { title: ReactNode; copyText?: string } | null {
  const status = row.epaIdRequirementStatus;
  if (status === EpaIdRequirementStatus.NeedsEpaId) {
    return {
      title: (
        <Stack gap={1}>
          <Typography variant="subtitle1" fontWeight="bold" fontSize="12px">
            Requires an EPA ID, but none is recorded
          </Typography>
          <Typography component="ul" sx={{ pl: 2, fontSize: "12px", "& li": { mb: 0.5 } }}>
            {row.epaIdRequirementReasons.map((reason) => (
              <li key={reason}>{EPA_ID_REQUIREMENT_REASON_LABELS[reason]}</li>
            ))}
          </Typography>
        </Stack>
      ),
    };
  }
  // Hover reveals the ID(s); click copies them. For the expired status the
  // expired IDs are the relevant ones.
  const isExpiredStatus = status === EpaIdRequirementStatus.EpaIdExpired;
  const epaIdText = findAlternateIds(row.facilityAlternateIds ?? [], [
    FacilityAlternateIdKnownKind.EPA,
  ])
    .filter((id) => id.value !== "" && isAlternateIdExpired(id) === isExpiredStatus)
    .map((id) => id.value)
    .join(", ");
  if (!epaIdText) {
    return null;
  }
  return {
    title: (
      <Typography fontSize="12px">
        {copied
          ? "Copied to clipboard"
          : `${epaIdText}${isExpiredStatus ? " (expired)" : ""} - click to copy`}
      </Typography>
    ),
    copyText: epaIdText,
  };
}

export function ImplementationFacilities({ tenantId }: { tenantId: string }) {
  const [selectedRows, setSelectedRows] = useState<Row[]>([]);

  const { featureFlags } = useFeatureFlags();
  const isWasteEnabled = featureFlags[FeatureFlagKey.WasteModule];
  const isTierIIEnabled = featureFlags[FeatureFlagKey.TierIIModule];

  const columns: OmnisearchGridColDef<Row>[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "Facility",
        filterKeyType: "facility",
        flex: 0.75,
        minWidth: 150,
        maxWidth: 500,
        renderCell: ({ row }) => (
          <Link href={`/o/${row.tenant?.id}/facilities/${row.id}/profile`} target="_blank">
            {row.name}
          </Link>
        ),
      },
      {
        field: "status",
        headerName: "Facility Status",
        minWidth: 130,
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(FacilityStatus).sort(),
        valueFormatter: (value) => prettyPrintEnumValue(value),
      },
      {
        field: "state",
        headerName: "State/Territory",
        maxWidth: 50,
      },
      {
        field: "wasteFeatureFlagOverride",
        headerName: "Waste",
        headerAlign: "center",
        align: "center",
        filterKeyType: "enum",
        enumValues: Object.values(FeatureFlagOverride),
        enumPresentationFunction: featureFlagOverrideLabel,
        renderCell({ row: { wasteFeatureFlagOverride: override } }) {
          if (override === FeatureFlagOverride.Enabled) {
            return (
              <Tooltip title="Waste is enabled">
                <Check />
              </Tooltip>
            );
          }

          if (override === FeatureFlagOverride.Disabled) {
            return (
              <Tooltip title="Waste is disabled">
                <BlockIcon />
              </Tooltip>
            );
          }

          if (override === FeatureFlagOverride.Inherit) {
            return (
              <Tooltip title={`Inheriting organization Waste setting (${isWasteEnabled})`}>
                {isWasteEnabled ? <FlagIcon /> : <OutlinedFlagIcon />}
              </Tooltip>
            );
          }
          return null;
        },
        minWidth: 80,
      },
      {
        field: "epaIdRequirementStatus",
        headerName: "EPA ID Status",
        sortable: false,
        minWidth: 150,
        filterKeyType: "enum",
        enumValues: Object.values(EpaIdRequirementStatus),
        enumPresentationFunction: epaIdRequirementStatusLabel,
        renderCell: ({ row }) => <EpaIdRequirementStatusCell row={row} />,
      },
      {
        field: "chemicalFeatureFlagOverride",
        headerName: "Chemicals",
        headerAlign: "center",
        align: "center",
        filterKeyType: "enum",
        enumValues: Object.values(FeatureFlagOverride),
        enumPresentationFunction: featureFlagOverrideLabel,
        renderCell({ row: { chemicalFeatureFlagOverride: override } }) {
          if (override === FeatureFlagOverride.Enabled) {
            return (
              <Tooltip title="Chemicals is enabled">
                <Check />
              </Tooltip>
            );
          }

          if (override === FeatureFlagOverride.Disabled) {
            return (
              <Tooltip title="Chemicals is disabled">
                <BlockIcon />
              </Tooltip>
            );
          }

          if (override === FeatureFlagOverride.Inherit) {
            return (
              <Tooltip title={`Inheriting organization Chemicals setting (${isTierIIEnabled})`}>
                {isTierIIEnabled ? <FlagIcon /> : <OutlinedFlagIcon />}
              </Tooltip>
            );
          }
          return null;
        },
        minWidth: 100,
      },
      {
        field: "customerFacilityId",
        headerName: "Customer Facility Id",
      },
      {
        field: "streetAddress1",
        headerName: "Address",
        flex: 0.5,
        minWidth: 200,
        valueGetter: (_, row) => {
          const { streetAddress1, streetAddress2, city, state, zip, country } = row;

          return compact([streetAddress1, streetAddress2, city, state, zip, country]).join(", ");
        },
      },
      {
        field: "credentialTypes",
        headerName: "Credential Types",
        sortable: false,
        align: "right",
        filterKeyType: "enum",
        enumValues: Object.values(CredentialKind),
        renderCell: ({ row }) => (
          <Stack direction="column" spacing={0.5} alignItems="flex-end" width="100%">
            {row.facilityCredentials?.map((fc, index) => (
              <Typography key={index} variant="body2" width="100%" align="left">
                {credentialKindToLabel(fc.credential.kind)}
              </Typography>
            ))}
          </Stack>
        ),
      },
      {
        field: "hasTierIICredential",
        headerName: "Has Tier II Credential?",
        headerAlign: "center",
        align: "center",
        sortable: false,
        filterKeyType: "boolean",
        renderCell({ row }) {
          if (row.hasTierIICredential) {
            return <Check />;
          }

          return null;
        },
      },
      {
        // Displays the list of credential statuses as icons and their last
        // verification check date
        field: "credentialStatus",
        headerName: "Verification Status",
        sortable: false,
        renderCell: renderVerificationStatusCell,
      },
      {
        field: "tags",
        headerName: "Tags",
        flex: 0.3,
        sortable: false,
        filterKeyType: "facilityTags",
        renderCell: ({ row }) => {
          const copyTags = [...row.tags];
          return <TagsDisplay tags={copyTags.sort((a, b) => a.name.localeCompare(b.name)) || []} />;
        },
      },
      {
        field: "hasStateId",
        headerName: "State ID",
        headerAlign: "center",
        sortable: false,
        align: "center",
        renderCell({ row }) {
          const isIdRequired = stateRequiresStateId(row.state);
          if (!row.state || !isIdRequired) {
            return null;
          }

          const hasAllStateIds = hasStateId(row.state, row.facilityAlternateIds ?? []);

          if (hasAllStateIds) {
            return <Check />;
          }

          return (
            <Tooltip title="Facility is missing necessary state ID(s)">
              <WarningAmberRounded />
            </Tooltip>
          );
        },
        filterKeyType: "enum",
        enumValues: ["Yes", "No", "NOT_REQUIRED"],
      },
      {
        field: "hasECHO",
        headerName: "ECHO",
        headerAlign: "center",
        align: "center",
        sortable: false,
        valueGetter: (_, row) => {
          const id = findAlternateId(row.facilityAlternateIds ?? [], [
            FacilityAlternateIdKnownKind.FRS,
          ]);
          return !!id;
        },
        renderCell({ value }) {
          if (value) {
            return <Check />;
          }
          return null;
        },
        filterKeyType: "boolean",
      },
      {
        field: "lastExtraction",
        headerName: "Last Extraction",
        sortable: false,
        valueGetter: (_, row) => row.jobs?.[0]?.updatedAt,
        valueFormatter: (_, row) =>
          row.jobs?.[0]?.updatedAt ? prettyPrintDateTime(row.jobs?.[0]?.updatedAt) : undefined,
        filterKeyType: "time",
        renderCell: ({ formattedValue, row: { id: facilityId } }) => {
          return (
            <Link
              href={`?omnisearch=facilityId%3A${facilityId}&tab=extractions`}
              target="_blank"
              className="MuiDataGrid-cell"
            >
              {formattedValue}
            </Link>
          );
        },
      },
      {
        field: "createdAt",
        headerName: "Created At",
        valueFormatter: (value) => prettyPrintDateTime(value),
        filterKeyType: "time",
      },
      {
        field: "updatedAt",
        headerName: "Updated",
        valueFormatter: (value) => prettyPrintDateTime(value),
        filterKeyType: "time",
      },
      {
        field: "collectionMode",
        headerName: "Collection Mode",
        type: "string",
        valueGetter(_, row) {
          switch (row.collectionMode) {
            case CollectionMode.Aggregate:
              return "Aggregate";
            case CollectionMode.Measurement:
              return "Measurement";
            case CollectionMode.MeasurementDefaulted:
              return "Measurement (Default)";
            case CollectionMode.AggregateDefaulted:
              return "Aggregate (Default)";
            default:
              return "Unknown";
          }
        },
      },
      {
        field: "issues",
        headerName: "Issues",
        flex: 0.15,
        renderCell: ({ row }) => {
          return <IssueCount issueCount={row.issueCount} />;
        },
      },
    ],
    // oxlint-disable-next-line exhaustive-deps -- Grandfathered in error. Future hooks should be exhaustive
    [isTierIIEnabled, isWasteEnabled]
  );

  const commandButtons = [
    <FacilitiesExportButton key="export" tenantId={tenantId} />,
    <Importer
      key="import"
      templateType={OneSchemaTemplateType.Facility}
      refetchQueries={["ImplementationFacilities"]}
    />,
    <FacilitiesBulkActionsMenu key="bulk-action-menu" selectedRows={selectedRows} />,
  ];

  return (
    <>
      <OmnisearchDataGrid
        persistenceKey="organization-facilities"
        isURLDriven
        withPadding={false}
        columns={columns}
        dataQuery={FACILITIES_WITH_JOBS_QUERY}
        defaultSearch={`tenantId:${tenantId}`}
        commandButtons={commandButtons}
        getRowHeight={(row) => {
          const itemCount = row.model.facilityCredentials?.length || 1;
          return itemCount * 38 - (itemCount - 1) * 12;
        }}
        noDataMessage="Organization has no facilities."
        getItems={(data) => data.facilities.items}
        getCount={(data) => data.facilities.count}
        onSelectedRowsChanged={setSelectedRows}
        initialState={{
          pinnedColumns: {
            left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, "name"],
          },
        }}
        columnVisibilityModel={{
          hasTierIICredential: false,
        }}
        excludeFilterColumns={["lastExtraction", "credentialStatus", "issues"]}
        additionalFilterColumns={[
          {
            key: "facility",
            header: "Facility Name",
            filterKeyType: "facility",
          },
          {
            header: "Has Credential",
            key: "hasCredential",
            filterKeyType: "boolean",
          },
          {
            header: "Show archived",
            key: "includeArchived",
            filterKeyType: "boolean",
          },
        ]}
      />
    </>
  );
}

/**
 * Renders the verification status for a facility's credentials along with the
 * last verification check date. The credentials will be rendered in the order
 * they are in in the facilityCredentials array.
 */
function renderVerificationStatusCell({ row }: { row: Row }) {
  return (
    <Stack direction="column" spacing={0.5}>
      {row.facilityCredentials?.map((fc, index) => (
        <Box key={index} display="flex" alignItems="center">
          <Tooltip title={prettyPrintEnumValue(fc.credential.verificationStatus)}>
            {getCredentialStatusIcon(fc.credential.verificationStatus)}
          </Tooltip>
          {fc.credential.lastVerificationCheckAt && (
            <Tooltip title={prettyPrintDateTime(fc.credential.lastVerificationCheckAt)}>
              <Typography variant="body2" component="span" sx={{ ml: 0.5 }}>
                {getUtcDisplayDate(fc.credential.lastVerificationCheckAt)}
              </Typography>
            </Tooltip>
          )}
        </Box>
      ))}
    </Stack>
  );
}

function getCredentialStatusIcon(verificationStatus: CredentialVerificationStatus) {
  switch (verificationStatus) {
    case CredentialVerificationStatus.Good:
      return <Check color="success" fontSize="small" />;
    case CredentialVerificationStatus.CurrentlyVerifying:
      return <HourglassTop color="warning" fontSize="small" />;
    case CredentialVerificationStatus.NotAttempted:
      return <QuestionMark color="warning" fontSize="small" />;
    case CredentialVerificationStatus.Locked:
      return <Lock color="error" fontSize="small" />;
    default:
      return <Error color="error" fontSize="small" />;
  }
}
